// src/pages/Analytics/AnalyticsPage.js
import React, { useEffect, useState, Fragment, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import { useAnalyticsDashboard, useHistoricalMetrics, useHighImpactObjects } from '../../hooks/useAnalytics';
import { apiRequest } from '../../utils/apiUtils';
import { analyticsAPI } from '../../api/enhancedApiService';
import { Transition } from '@headlessui/react';
import {
  ChartBarIcon,
  ExclamationCircleIcon,
  SparklesIcon,
  ArrowRightIcon,
  ArrowPathIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import {
  ClockIcon,
  ArrowTrendingUpIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/solid';

// Import components
import HistoricalMetricsDashboard from '../../components/analytics/HistoricalMetricsDashboard';
import MetricCard from '../../components/analytics/MetricCard';
import TrendChart from '../../components/analytics/TrendChart';
import InsightCard from '../../components/analytics/InsightCard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DataObservabilityDashboard from '../../components/analytics/DataObservabilityDashboard';
import { formatDate } from '../../utils/formatting';

const AnalyticsPage = () => {
  const { activeConnection } = useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();
  const [timeframe, setTimeframe] = useState(30); // Default to 30 days
  const [tableList, setTableList] = useState([]);
  const [tableSearchTerm, setTableSearchTerm] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('overview');

  // Table filter state
  const [selectedTables, setSelectedTables] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false); // True when filter is active
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);

  // Update breadcrumbs when connection changes
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Analytics', href: '/analytics' },
    ]);
  }, [updateBreadcrumbs]);


  // Fetch analytics overview data for the dashboard
  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['analytics-overview', activeConnection?.id, timeframe],
    queryFn: () => analyticsAPI.getAnalyticsOverview(activeConnection?.id, { days: timeframe }),
    enabled: !!activeConnection?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 60 * 60 * 1000, // 1 hour (renamed from cacheTime in RQ v5)
    refetchOnWindowFocus: false,
  });

  // Fetch high impact objects
  const { data: highImpactData, isLoading: isHighImpactLoading } = useHighImpactObjects(
    activeConnection?.id,
    { limit: 5, enabled: !!activeConnection?.id }
  );

  // Fetch quality score metrics
  const { data: qualityScoreData, isLoading: isQualityScoreLoading } = useHistoricalMetrics(
    activeConnection?.id,
    {
      metricName: 'quality_score',
      days: timeframe,
      enabled: !!activeConnection?.id
    }
  );

  // Handle timeframe change
  const handleTimeframeChange = (days) => {
    setTimeframe(days);
  };


  // Extract unique table names from dashboard data
  const availableTables = React.useMemo(() => {
    if (!dashboardData || !dashboardData.row_count_trends) return [];

    // Get unique table names
    const tableSet = new Set();
    dashboardData.row_count_trends.forEach(item => {
      if (item.table_name) {
        tableSet.add(item.table_name);
      }
    });

    return Array.from(tableSet).sort();
  }, [dashboardData]);

  // Final filtered tables list (combine from both sources)
  const filteredTables = tableSearchTerm
    ? availableTables.filter(tableName =>
        tableName.toLowerCase().includes(tableSearchTerm.toLowerCase())
      )
    : availableTables;

  // Table name accessor function to handle different table object formats
  const getTableName = (table) => {
    return table.name || table.table_name || table.table_id || '';
  };

  // Handle table selection for filtering
  const handleTableSelection = (tableName) => {
    setSelectedTables(prev => {
      // If already selected, remove it
      if (prev.includes(tableName)) {
        return prev.filter(t => t !== tableName);
      }
      // Otherwise add it
      return [...prev, tableName];
    });
  };

  // Toggle filtering mode
  const toggleFiltering = () => {
    if (isFiltering) {
      // Turn off filtering
      setIsFiltering(false);
      setSelectedTables([]);
      setShowFilterDropdown(false);
    } else {
      // Turn on filtering
      setIsFiltering(true);
      setShowFilterDropdown(true);
    }
  };

  // Handle closing the dropdown
  const handleCloseDropdown = () => {
    setShowFilterDropdown(false);
  };

  if (!activeConnection) {
    return (
      <div className="rounded-lg bg-white shadow p-6 text-center">
        <ChartBarIcon className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-secondary-900">No Connection Selected</h3>
        <p className="mt-2 text-secondary-500">
          Please select a database connection to view analytics.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Navigation Tabs */}
      <div className="bg-white rounded-lg shadow overflow-visible">
        <div className="px-4 py-5 sm:px-6">
          <h1 className="text-2xl font-bold text-secondary-900">Analytics Dashboard</h1>
          <p className="mt-1 text-secondary-500">
            Data quality insights for {activeConnection.name}
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="border-t border-secondary-200 px-4 py-3 sm:px-6 relative overflow-visible">
          <div className="flex space-x-4 pb-1">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap ${
                activeTab === 'overview' 
                  ? 'bg-primary-100 text-primary-700' 
                  : 'text-secondary-600 hover:text-secondary-800 hover:bg-secondary-50'
              }`}
            >
              <span className="flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-1" />
                Overview
              </span>
            </button>

          </div>
        </div>
      </div>

      {/* Dashboard Controls - Time & Table Filters */}
      <div className="flex justify-between items-center bg-white rounded-lg shadow p-3">
        {/* Table Filter */}
        <div className="relative">
          <div className="flex items-center space-x-2">
            {/* Table filter toggle button */}
            <button
              className={`flex items-center px-3 py-2 border rounded-md text-sm ${
                isFiltering
                  ? 'bg-primary-50 border-primary-300 text-primary-700'
                  : 'border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50'
              }`}
              onClick={toggleFiltering}
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              {isFiltering
                ? `Filtering: ${selectedTables.length} Tables`
                : 'Filter Tables'}
            </button>

            {/* Clear selection button (only shown when filtering) */}
            {isFiltering && selectedTables.length > 0 && (
              <button
                className="px-2 py-1 text-xs text-secondary-600 hover:text-secondary-800 hover:underline"
                onClick={() => setSelectedTables([])}
              >
                Clear selection
              </button>
            )}
          </div>

          {/* Table Selection Dropdown */}
          {showFilterDropdown && (
            <div className="absolute left-0 mt-2 w-64 bg-white border border-secondary-200 rounded-md shadow-lg z-10">
              <div className="p-3 border-b border-secondary-200 flex justify-between items-center">
                <h3 className="text-sm font-medium text-secondary-900">Select Tables</h3>
                <button
                  onClick={handleCloseDropdown}
                  className="text-secondary-400 hover:text-secondary-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="p-2 border-b border-secondary-200">
                <div className="relative">
                  <input
                    type="text"
                    className="w-full px-3 py-2 text-sm border border-secondary-300 rounded-md"
                    placeholder="Search tables..."
                    value={tableSearchTerm}
                    onChange={(e) => setTableSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto py-1">
                {filteredTables.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-secondary-500">No tables available</div>
                ) : (
                  filteredTables.map((tableName, index) => (
                    <button
                      key={index}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        selectedTables.includes(tableName) 
                          ? 'bg-primary-50 text-primary-700' 
                          : 'text-secondary-700 hover:bg-secondary-50'
                      }`}
                      onClick={() => handleTableSelection(tableName)}
                    >
                      {tableName}
                      {selectedTables.includes(tableName) && (
                        <span className="ml-2"></span>
                      )}
                    </button>
                  ))
                )}
              </div>
              <div className="p-2 border-t border-secondary-200 flex justify-end">
                <button
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
                  onClick={handleCloseDropdown}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Time range selector */}
        <div className="flex items-center space-x-2 bg-white rounded-md p-1">
          <button
            onClick={() => handleTimeframeChange(7)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              timeframe === 7
                ? 'bg-primary-100 text-primary-700'
                : 'text-secondary-500 hover:text-secondary-700'
            }`}
          >
            7 Days
          </button>
          <button
            onClick={() => handleTimeframeChange(30)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              timeframe === 30
                ? 'bg-primary-100 text-primary-700'
                : 'text-secondary-500 hover:text-secondary-700'
            }`}
          >
            30 Days
          </button>
          <button
            onClick={() => handleTimeframeChange(90)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              timeframe === 90
                ? 'bg-primary-100 text-primary-700'
                : 'text-secondary-500 hover:text-secondary-700'
            }`}
          >
            90 Days
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' ? (
        <DataObservabilityDashboard 
          connectionId={activeConnection.id}
          timeframe={timeframe}
          analyticsData={dashboardData}
          isLoading={isDashboardLoading}
        />
      ) : null}
    </div>
  );
};

export default AnalyticsPage;
